<template>
  <div class="consignee">
    <div
      class="consignee-head--title black--text font-weight-medium"
      :class="{ 'fs-18': breakpointXs, 'fs-25': !breakpointXs }"
    >
      <DashboardBreadcrumb :items="breadCrumbItems" />
    </div>
    <div class="consignee-body mt-4">
      <v-card elevation="4" class="px-8 py-6">
        <v-row
          class="loader fill-height py-6"
          align="center"
          justify="center"
          v-if="isConsigneeFormLoading || retrievingEditExportJob"
        >
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </v-row>
        <div v-else>
          <div class="consignee-body--title fw-600" :class="{ 'fs-18': breakpointXs, 'fs-25': !breakpointXs }">
            <span>Consignee Details</span>
            <span>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn @click="makeFieldsNull" class="float-right" color="blue-grey" v-on="on">
                    <span class="font-weight-bold white--text ">Reset</span>
                    <v-icon class="font-weight-bold white--text fs-15 ml-2">fas fa-undo</v-icon>
                  </v-btn>
                </template>
                {{ resetIconMessage() }}
              </v-tooltip>
            </span>
          </div>
          <div class="consignee-body--form pt-3">
            <ValidationObserver @keydown.tab="focusNext" v-slot="{ passes }">
              <v-row>
                <v-col cols="12" :xs="12" :sm="12" :md="6" :lg="6">
                  <TextField
                    v-model="consigneeName"
                    rules="required|max:35"
                    showIcons
                    required
                    :showOnlyErrorIcons="true"
                    :loading="isConsigneeFormLoading || savingExportJob"
                    label="Consignee Name (To Order)"
                  />
                </v-col>
                <v-col cols="12" :xs="12" :sm="12" :md="6" :lg="6">
                  <AutoComplete
                    v-model="consigneeCountryDropdownValue"
                    :items="countries"
                    item-text="name"
                    rules="required"
                    :return-object="true"
                    :loading="isConsigneeFormLoading || savingExportJob"
                    label="Consignee Country"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" :xs="12" :sm="6" :md="6" :lg="6">
                  <v-textarea
                    v-model="consigneeAddress"
                    showIcons
                    filled
                    rows="4"
                    :validate-on-blur="true"
                    aria-required
                    required
                    :showOnlyErrorIcons="true"
                    :loading="isConsigneeFormLoading || savingExportJob"
                    label="Consignee Address 1"
                  />
                </v-col>
                <v-col cols="12" :xs="12" :sm="6" :md="6" :lg="6">
                  <v-textarea
                    v-model="notifyParty"
                    filled
                    rows="4"
                    showIcons
                    :showOnlyErrorIcons="true"
                    :loading="isConsigneeFormLoading || savingExportJob"
                    label="Consignee Address 2 (Notify Party)"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-textarea
                    v-model="marks"
                    :loading="isConsigneeFormLoading || savingExportJob"
                    filled
                    rows="4"
                    label="Marks and Instructions"
                  ></v-textarea>
                </v-col>
              </v-row>
              <div class="d-sm-flex justify-end" :class="{ 'text-center': breakpointXs }">
                <v-row>
                  <v-col cols="12" :xs="12" :sm="6" :md="6" :lg="6">
                    <div class="d-sm-flex justify-start" :class="{ 'text-center': breakpointXs }">
                      <v-btn
                        width="140"
                        color="info"
                        large
                        class="white--text"
                        :loading="isConsigneeFormLoading || savingExportJob"
                        @click="$router.push({ name: 'dashboard.export.exporter' })"
                      >
                        <v-icon class="font-weight-bold fs-15 mr-2">fa fa-arrow-circle-left</v-icon>
                        <span class="font-weight-bold fs-15">Previous</span>
                      </v-btn>
                    </div>
                  </v-col>
                  <v-col cols="12" :xs="12" :sm="6" :md="6" :lg="6">
                    <div class="d-sm-flex justify-end" :class="{ 'text-center': breakpointXs }">
                      <v-btn
                        width="140"
                        color="button-primary"
                        large
                        class="white--text"
                        :loading="isConsigneeFormLoading || savingExportJob"
                        @click="passes(saveConsignee)"
                      >
                        <span class="font-weight-bold fs-15">Save</span>
                      </v-btn>
                      <div class="ml-2 mr-2 my-1"></div>
                      <v-btn
                        width="140"
                        color="info"
                        large
                        class="white--text"
                        :loading="isConsigneeFormLoading || savingExportJob"
                        @click="$router.push({ name: 'dashboard.export.invoice-products' })"
                      >
                        <span class="font-weight-bold fs-15">Next</span>
                        <v-icon class="font-weight-bold fs-15 ml-2">fa fa-arrow-circle-right</v-icon>
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </ValidationObserver>
            <ConsigneeModal
              @selected-consignee="selectedConsignee"
              :consignees="consignees"
              :clickedButtonFieldId="clickedButtonFieldId"
              v-if="showConsigneeModal"
              @close-consignee-modal="showConsigneeModal = false"
            />
          </div>
        </div>
      </v-card>
    </div>
  </div>
</template>
<script>
import DashboardBreadcrumb from "@/modules/DashboardModule/components/DashboardBreadcrumb";
import ConsigneeModal from "@/modules/DashboardModule/modules/ImportExportModule/modules/ExportModule/components/ConsigneeModal";
import { ValidationObserver } from "vee-validate";
import TextField from "@/components/TextField";
import AutoComplete from "@/components/AutoComplete";
import * as MT from "@/modules/DashboardModule/modules/ImportExportModule/modules/ExportModule/store/mutationTypes";
import * as AT from "@/modules/DashboardModule/modules/ImportExportModule/modules/ExportModule/store/actionTypes";
import handleExportJobMixin from "@/modules/DashboardModule/modules/ImportExportModule/mixins/handleExportJobMixin";
import commonMethodsMixin from "@/modules/commonMethodsMixin";
export default {
  name: "Consignee",
  components: {
    DashboardBreadcrumb,
    ConsigneeModal,
    ValidationObserver,
    TextField,
    AutoComplete
  },
  data() {
    return {
      consignees: [],
      showConsigneeModal: false,
      isConsigneeFormLoading: false,
      clickedButtonFieldId: "",
      consigneeAddress: "",
      notifyParty: ""
    };
  },
  mounted() {
    this.consigneeAddress = this.address1;
    this.notifyParty = this.address2;
  },
  mixins: [commonMethodsMixin, handleExportJobMixin],
  methods: {
    makeFieldsNull() {
      this.consigneeName = null;
      this.consigneeCountryDropdownValue = "";
      this.consigneeAddress = "";
      this.notifyParty = "";
      this.address3 = null;
      this.address4 = null;
      this.marks = null;
    },
    getConsignees() {
      this.loading = true;
      const country = this.$store.state.ExportModuleStore.sbMain.consigneeCountryCode;
      this.$store
        .dispatch(AT.GET_CONSIGNEES, country)
        .then(res => {
          if (res) {
            this.consignees = res.data;
          }
        })
        .catch(err => {
          this.handleError(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    consigneeModalHandler(id) {
      this.showConsigneeModal = true;
      this.clickedButtonFieldId = id;
    },
    selectedConsignee(data) {
      if (parseInt(this.clickedButtonFieldId) === 1) {
        this.address1 = data.selectedItem.consignee_description;
      } else if (parseInt(this.clickedButtonFieldId) === 2) {
        this.address2 = data.selectedItem.consignee_description;
      } else if (parseInt(this.clickedButtonFieldId) === 3) {
        this.address3 = data.selectedItem.consignee_description;
      } else if (parseInt(this.clickedButtonFieldId) === 4) {
        this.address4 = data.selectedItem.consignee_description;
      }
      this.showConsigneeModal = false;
    },
    saveConsignee() {
      this.address1 = this.consigneeAddress;
      this.address2 = this.notifyParty;
    if (this.consigneeAddress.length === 0 || this.consigneeAddress.length >= 71) {
        const errorMessage = "Consignee is required, must be less than 70 characters";
        this.throwErrorMessage(errorMessage);
        return;
      } else if (this.notifyParty.length <= 0) {
          this.$store.commit(MT.SET_SBMAIN_CONSIGNEE_ADDRESS3, {
            value: this.address1.substring(0,34)
          });
          this.$store.commit(MT.SET_SBMAIN_CONSIGNEE_ADDRESS4, {
            value: this.address1.substring(34,69)
          });
        this.$store.commit(MT.SET_INVOICES_BUYER_DETAILS, this.address1);
        }
      this.saveExportJob();
    }
  },
  computed: {
    consigneeName: {
      get() {
        const consigneeName = this.$store.state.ExportModuleStore.sbMain.consigneeName;
        if (consigneeName === null) {
          return "TO ORDER";
        }
        return this.$store.state.ExportModuleStore.sbMain.consigneeName;
      },
      set(value) {
        this.$store.commit(MT.SET_SBMAIN_CONSIGNEE_NAME, { value });
        return;
      }
    },
    consigneeCountryDropdownValue: {
      get() {
        return this.$store.state.ExportModuleStore.sbMain.consigneeCountryCodeDropdownValue;
      },
      set(consigneeCountryCodeObject) {
        this.$store.commit(MT.SET_SBMAIN_CONSIGNEE_COUNTRY_CODE_DROPDOWN_VALUE, {
          value: consigneeCountryCodeObject.name
        });
        this.consigneeCountry = consigneeCountryCodeObject.code;
        return;
      }
    },
    consigneeCountry: {
      get() {
        return this.$store.state.ExportModuleStore.sbMain.consigneeCountryCode;
      },
      set(value) {
        this.$store.commit(MT.SET_SBMAIN_CONSIGNEE_COUNTRY_CODE, { value });
      }
    },
    address2: {
      get() {
        const consigneeAddress3 = this.$store.state.ExportModuleStore.sbMain.consigneeAddress3;
        const consigneeAddress4 = this.$store.state.ExportModuleStore.sbMain.consigneeAddress4;
        return [consigneeAddress3, consigneeAddress4].join("");
      },
      set(address2Value) {
        if (address2Value.length < 35) {
          this.$store.commit(MT.SET_SBMAIN_CONSIGNEE_ADDRESS3, {
            value: address2Value.substring(0, address2Value.length)
          });
          this.$store.commit(MT.SET_SBMAIN_CONSIGNEE_ADDRESS4, {
            value: null
          });
        } else if (address2Value.length >= 35 && address2Value.length < 70) {
          this.$store.commit(MT.SET_SBMAIN_CONSIGNEE_ADDRESS3, { value: address2Value.substring(0, 35) });
          this.$store.commit(MT.SET_SBMAIN_CONSIGNEE_ADDRESS4, {
            value: address2Value.substring(35, address2Value.length)
          });
        }
        this.$store.commit(MT.SET_INVOICES_BUYER_DETAILS, address2Value);
        return;
      }
    },
    address1: {
      get() {
        const consigneeAddress1 = this.$store.state.ExportModuleStore.sbMain.consigneeAddress1;
        const consigneeAddress2 = this.$store.state.ExportModuleStore.sbMain.consigneeAddress2;
        return [consigneeAddress1, consigneeAddress2].join("");
      },
      set(address1) {
        if (address1.length < 35) {
          this.$store.commit(MT.SET_SBMAIN_CONSIGNEE_ADDRESS1, {
            value: address1.substring(0, address1.length)
          });
          this.$store.commit(MT.SET_SBMAIN_CONSIGNEE_ADDRESS2, {
            value: null
          });
        } else if (address1.length >= 35 && address1.length < 70) {
          this.$store.commit(MT.SET_SBMAIN_CONSIGNEE_ADDRESS1, { value: address1.substring(0, 35) });
          this.$store.commit(MT.SET_SBMAIN_CONSIGNEE_ADDRESS2, {
            value: address1.substring(35, address1.length)
          });
        }
        return;
      }
    },

    address3: {
      get() {
        return this.$store.state.ExportModuleStore.sbMain.consigneeAddress3;
      },
      set(value) {
        this.$store.commit(MT.SET_SBMAIN_CONSIGNEE_ADDRESS3, { value });
      }
    },
    address4: {
      get() {
        return this.$store.state.ExportModuleStore.sbMain.consigneeAddress4;
      },
      set(value) {
        this.$store.commit(MT.SET_SBMAIN_CONSIGNEE_ADDRESS4, { value });
      }
    },
    marks: {
      get() {
        return this.$store.state.ExportModuleStore.sbMain.marksAndNos;
      },
      set(value) {
        this.$store.commit(MT.SET_SBMAIN_MARKS_AND_NOS, { value });
      }
    },

    countries() {
      return this.$store.getters.countries;
    },
    breadCrumbItems() {
      return [
        {
          title: "Dashboard",
          route: "dashboard.home",
          children: [
            {
              title: "Export",
              route: "dashboard.export",
              children: [
                {
                  title: "Consignee",
                  route: "dashboard.export.consignee"
                }
              ]
            }
          ]
        }
      ];
    }
  }
};
</script>
